/**
 * Sewing machine listing spec (sewing-machine-taxonomy Phase 1).
 *
 * Canonical id: `sewing-machine`. Three legacy peer ids are aliases.
 * Required: `sewing_machine_type` ∈ manual | electric | industrial.
 * Title: `{Brand?} {Type} Sewing Machine` — brand optional from sell brand field.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const SEWING_MACHINE_TYPES = [
  "manual",
  "electric",
  "industrial",
] as const;
export type SewingMachineType = (typeof SEWING_MACHINE_TYPES)[number];

export const CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID =
  "sewing-machine" as const;

export const LEGACY_SEWING_MACHINE_SUBCATEGORY_IDS = [
  "manual-sewing-machine",
  "electric-sewing-machine",
  "industrial-sewing-machine",
] as const;

export type LegacySewingMachineSubcategoryId =
  (typeof LEGACY_SEWING_MACHINE_SUBCATEGORY_IDS)[number];

export const SEWING_MACHINE_SUBCATEGORY_IDS = [
  CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
  ...LEGACY_SEWING_MACHINE_SUBCATEGORY_IDS,
] as const;

export type SewingMachineSubcategoryId =
  (typeof SEWING_MACHINE_SUBCATEGORY_IDS)[number];

export type LegacySewingMachineSubcategoryMapping = {
  legacyId: LegacySewingMachineSubcategoryId;
  canonicalId: typeof CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID;
  sewingMachineType: SewingMachineType;
};

/** Locked Phase 0 alias map (SIO + reference backfill). */
export const LEGACY_SEWING_MACHINE_SUBCATEGORY_MAP: readonly LegacySewingMachineSubcategoryMapping[] =
  [
    {
      legacyId: "manual-sewing-machine",
      canonicalId: CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
      sewingMachineType: "manual",
    },
    {
      legacyId: "electric-sewing-machine",
      canonicalId: CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
      sewingMachineType: "electric",
    },
    {
      legacyId: "industrial-sewing-machine",
      canonicalId: CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID,
      sewingMachineType: "industrial",
    },
  ] as const;

const mapByLegacyId = new Map<string, LegacySewingMachineSubcategoryMapping>(
  LEGACY_SEWING_MACHINE_SUBCATEGORY_MAP.map((row) => [row.legacyId, row]),
);

export type SewingMachineSpecFormState = {
  sewingMachineType: SewingMachineType | "";
};

export function defaultSewingMachineSpecFormState(): SewingMachineSpecFormState {
  return { sewingMachineType: "" };
}

export function isSewingMachineType(
  value: unknown,
): value is SewingMachineType {
  return (
    typeof value === "string" &&
    (SEWING_MACHINE_TYPES as readonly string[]).includes(value)
  );
}

export function isSewingMachineSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is SewingMachineSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (SEWING_MACHINE_SUBCATEGORY_IDS as readonly string[]).includes(
      subcategoryId,
    )
  );
}

export function isCanonicalSewingMachineSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID;
}

export function getSewingMachineSpecSectionLabel(): string {
  return "Sewing machine details";
}

export function resolveCanonicalSewingMachineSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  if (subcategoryId === CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID) {
    return CANONICAL_SEWING_MACHINE_SUBCATEGORY_ID;
  }
  return mapByLegacyId.get(subcategoryId)?.canonicalId ?? null;
}

export function getLegacySewingMachineSubcategoryMapping(
  subcategoryId: string | null | undefined,
): LegacySewingMachineSubcategoryMapping | null {
  if (!subcategoryId) return null;
  return mapByLegacyId.get(subcategoryId) ?? null;
}

export function inferSewingMachineTypeFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): SewingMachineType | null {
  return (
    getLegacySewingMachineSubcategoryMapping(subcategoryId)
      ?.sewingMachineType ?? null
  );
}

/**
 * D4 title inference. Precedence: industrial > electric > manual.
 * Returns null when no type word is present.
 */
export function inferSewingMachineTypeFromTitle(
  title: string | null | undefined,
): SewingMachineType | null {
  const t = String(title ?? "").toLowerCase();
  if (/\bindustrial\b/.test(t)) return "industrial";
  if (/\belectric\b/.test(t)) return "electric";
  if (/\bmanual\b/.test(t)) return "manual";
  return null;
}

/**
 * Resolve type from legacy id defaults, then title override (D4).
 * Canonical id without title cue defaults to null (sell form must choose).
 */
export function resolveSewingMachineTypeForLegacySubcategory(
  subcategoryId: string | null | undefined,
  title?: string | null,
): SewingMachineType | null {
  const fromTitle = inferSewingMachineTypeFromTitle(title);
  const fromLegacy =
    inferSewingMachineTypeFromLegacySubcategoryId(subcategoryId);
  if (fromTitle) return fromTitle;
  if (fromLegacy) return fromLegacy;
  return null;
}

export function formatSewingMachineTypeTitleWord(
  type: SewingMachineType,
): string {
  switch (type) {
    case "manual":
      return "Manual";
    case "electric":
      return "Electric";
    case "industrial":
      return "Industrial";
  }
}

/**
 * Locked title canon: `{Manual|Electric|Industrial}` → brand? → Sewing Machine
 */
export function composeSewingMachineTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const type = attrs.sewing_machine_type;
  if (!isSewingMachineType(type)) return "";

  return composeListingTitleFromSlotsWithIdentity(
    {
      form: formatSewingMachineTypeTitleWord(type),
      product: "Sewing Machine",
    },
    identity,
  );
}

export function buildSewingMachineAttributesFromForm(
  form: SewingMachineSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isSewingMachineType(form.sewingMachineType)) {
    out.sewing_machine_type = form.sewingMachineType;
  }
  return out;
}

export function getSewingMachineSpecFormValidationMessage(
  form: SewingMachineSpecFormState,
): string | undefined {
  const issues = validateStoredSewingMachineAttributes(
    buildSewingMachineAttributesFromForm(form),
  );
  return issues[0]?.message;
}

export type SewingMachineFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored attrs (+ legacy alias fallback). */
export function sewingMachineFormFromAttributes(
  attributes: unknown,
  options?: SewingMachineFormHydrateOptions,
): SewingMachineSpecFormState {
  const base = defaultSewingMachineSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  let sewingMachineType: SewingMachineType | "" = isSewingMachineType(
    attrs.sewing_machine_type,
  )
    ? attrs.sewing_machine_type
    : base.sewingMachineType;

  if (!sewingMachineType && options?.subcategoryId) {
    const resolved = resolveSewingMachineTypeForLegacySubcategory(
      options.subcategoryId,
      options.title,
    );
    if (resolved) sewingMachineType = resolved;
  }

  return { sewingMachineType };
}

export function validateStoredSewingMachineAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const type = attrs.sewing_machine_type;

  if (type === undefined || type === null || type === "") {
    issues.push({
      key: "sewing_machine_type",
      code: "required",
      message: "Choose Manual, Electric, or Industrial.",
    });
  } else if (!isSewingMachineType(type)) {
    issues.push({
      key: "sewing_machine_type",
      code: "invalid_enum",
      message: "Type must be Manual, Electric, or Industrial.",
    });
  }

  return issues;
}

/** Merge type into existing attributes for retag/backfill. */
export function mergeSewingMachineTypeAttr(
  attributes: unknown,
  sewingMachineType: SewingMachineType,
): Record<string, unknown> {
  const base =
    attributes && typeof attributes === "object" && !Array.isArray(attributes)
      ? { ...(attributes as Record<string, unknown>) }
      : {};
  base.sewing_machine_type = sewingMachineType;
  return base;
}
